import { useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { useMe } from "@/context/MeContext";
import { canViewAll, isStaff } from "@/lib/format";
import NotificationBell from "@/components/NotificationBell";
import { HelpGuides } from "@/components/HelpGuides";

function NavItem({ to, label, testId, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `text-[0.68rem] tracking-[0.24em] uppercase py-2 border-b transition-colors duration-300 whitespace-nowrap ${
          isActive ? "text-[#C6A55C] border-[#C6A55C]" : "text-white/85 border-transparent hover:text-white"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

export default function AppShell() {
  useEffect(() => { document.title = "JSG Portal"; }, []);
  const { me } = useMe();
  const staff = isStaff(me);
  const viewAll = canViewAll(me);
  const links = [
    { to: "/", label: viewAll ? "All Requests" : "My Trips", testId: "nav-requests", end: true },
    { to: "/trips/new", label: staff ? "New Request" : "Request a Quote", testId: "nav-new-request" },
    { to: "/jet-cards", label: viewAll ? "Jet Card Requests" : "Jet Card Agreement", testId: "nav-jet-cards" },
    { to: "/tracking", label: "Flight Tracking", testId: "nav-tracking" },
    ...(staff ? [{ to: "/users", label: me.role === "admin" ? "Users" : "Brokers", testId: "nav-users" }] : []),
    ...(staff ? [{ to: "/operators", label: "Operators", testId: "nav-operators" }] : []),
    ...(staff ? [{ to: "/quote-inbox", label: "Quote Inbox", testId: "nav-quote-inbox" }] : []),
    ...(me.role === "admin" ? [{ to: "/aircraft", label: "Aircraft", testId: "nav-aircraft" }] : []),
    { to: "/profile", label: "Profile", testId: "nav-profile" },
  ];
  return (
    <div className="min-h-screen bg-[#090A0D] overflow-x-clip">
      <header className="on-navy app-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center gap-8">
          <NavLink to="/" data-testid="nav-logo" className="shrink-0"><img src="/brand/logo-silver.png" alt="JetSet Group Inc" className="h-12 w-auto" /></NavLink>
          <nav className="hidden md:flex items-center gap-7">
            {links.map((l) => <NavItem key={l.to} {...l} />)}
          </nav>
          <div className="ml-auto flex items-center gap-3 sm:gap-4 min-w-0">
            <span className="inline-block whitespace-nowrap text-[0.55rem] sm:text-[0.62rem] tracking-[0.14em] sm:tracking-[0.22em] uppercase text-white border border-white/40 px-2 sm:px-3 py-1 sm:py-1.5" data-testid="role-chip">
              {me.role_label}
            </span>
            <NotificationBell />
            <UserButton />
          </div>
        </div>
        <nav className="md:hidden flex gap-6 px-4 overflow-x-auto no-scrollbar border-t border-white/[0.05]">
          {links.map((l) => <NavItem key={l.to} {...l} testId={`${l.testId}-mobile`} />)}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Outlet />
      </main>
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-white/[0.06] text-[11px] text-zinc-600 leading-relaxed">
        All flights arranged by JetSet Group Inc. are operated by FAA Part 135 carriers unless noted. Operators providing services for
        JetSet Group Inc. clients must meet standards set forth by the Federal Aviation Administration (FAA). JetSet Group Inc. does not
        manage or operate aircraft on behalf of our clients. We represent our clients in finding aircraft for their needs while abiding by
        FAA Part 295 · 855-5JETSET · charter@jetsetgrp.com
        <HelpGuides staff={isStaff(me)} />
      </footer>
    </div>
  );
}
